import { useMemo } from 'react';
import type { DocumentData } from 'firebase/firestore';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { labelClassName } from '@/components/ui/label';
import { useT } from '@/lib/i18n';
import { cn, fmtBytes, fmtMbps } from '@/lib/utils';
import {
  feasibilityTone, parseRecommendations, sensitivityTone, severityTone,
  type HardwareOption,
} from '@/lib/catalog';
import {
  ArtifactLoader, Bullets, ContractTooOld, DataTable, Metric, Section, ToneBadge, contractLabel,
  useArtifactJson,
} from './CatalogShared';

/**
 * Onglet « Préconisations » — livrable client du contrat 1.2
 * (`json/recommendations.json`, écrit par le pipeline).
 *
 * Il répond à quatre questions : où héberger l'IA et pourquoi, quel serveur
 * acheter (trois options chiffrées et leurs hypothèses), par quelles sources
 * commencer, et ce qui bloque encore. La dernière section dit ce que l'audit
 * n'a PAS pu mesurer — elle n'est jamais masquée.
 */
export function RecommendationsTab({ auditId, module, schemaVersion }: {
  auditId?: string;
  module?: DocumentData;
  schemaVersion?: string | null;
}) {
  const { t } = useT();
  const artifact = useArtifactJson(auditId, module as { json?: unknown; jsonPath?: string } | undefined);
  const reco = useMemo(() => parseRecommendations(artifact.json), [artifact.json]);

  if (!reco) {
    if (artifact.canLoad && artifact.path) {
      return (
        <ArtifactLoader
          path={artifact.path}
          label={t('audit.catalog.reco.load')}
          loading={artifact.loading}
          error={artifact.error}
          onLoad={artifact.load}
        />
      );
    }
    return <ContractTooOld title={t('audit.reco.unavailable.title')} schemaVersion={schemaVersion} />;
  }

  const { ingestion, sizingBasis } = reco;

  return (
    <div className="space-y-6">
      <Section
        title={t('audit.reco.deployment')}
        aside={reco.confidence
          ? <Badge variant="info">{t('audit.reco.confidence', { level: contractLabel(t, 'audit.reco.confidence.' + reco.confidence, reco.confidence) })}</Badge>
          : undefined}
      >
        <div className="font-display text-2xl text-ink-900 dark:text-white">
          {contractLabel(t, 'audit.reco.mode.' + reco.mode, reco.mode)}
        </div>

        <div className={labelClassName()}>{t('audit.reco.reasons')}</div>
        <ul className="space-y-2 text-sm">
          {reco.reasons.map(r => (
            <li key={r.code} className="flex flex-wrap items-start gap-2">
              <Badge variant="neutral">{contractLabel(t, 'audit.reco.favours.' + r.favours, r.favours)}</Badge>
              <span className="flex-1 min-w-[12rem]">{r.detail}</span>
            </li>
          ))}
          {reco.reasons.length === 0 && <li className="text-ink-500">{t('audit.catalog.empty')}</li>}
        </ul>

        {reco.alternatives.length > 0 && (
          <>
            <div className={labelClassName()}>{t('audit.reco.alternatives')}</div>
            <DataTable
              empty={t('audit.catalog.empty')}
              headers={[t('audit.reco.deployment'), t('audit.catalog.col.detail')]}
              rows={reco.alternatives.map(a => [
                contractLabel(t, 'audit.reco.mode.' + a.mode, a.mode),
                <>
                  <div>{a.detail}</div>
                  <div className="mt-1 text-xs text-ink-500"><Bullets items={a.conditions} /></div>
                </>,
              ])}
            />
          </>
        )}
      </Section>

      <Section title={t('audit.reco.hardware')} description={t('audit.reco.hardware.desc')}>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {reco.options.map(option => <OptionCard key={option.tier} option={option} />)}
        </div>
        {reco.options.length === 0 && <div className="text-sm text-ink-500">{t('audit.catalog.empty')}</div>}
      </Section>

      <Section title={t('audit.reco.sizing')}>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          <Metric label={t('audit.reco.sizing.documents')} value={fmtCount(sizingBasis.documents)} />
          <Metric label={t('audit.reco.sizing.bytes')} value={sizingBasis.bytesNet != null ? fmtBytes(sizingBasis.bytesNet) : '—'} />
          <Metric label={t('audit.reco.sizing.tokens')} value={fmtCount(sizingBasis.tokens)} />
          <Metric label={t('audit.reco.sizing.chunks')} value={fmtCount(sizingBasis.chunks)} />
          <Metric label={t('audit.reco.sizing.vectors')} value={fmtCount(sizingBasis.vectors)} />
          <Metric label={t('audit.reco.sizing.index')} value={sizingBasis.indexBytes != null ? fmtBytes(sizingBasis.indexBytes) : '—'} />
        </div>
      </Section>

      <Section title={t('audit.reco.ingestion')}>
        {ingestion.hoursInitial == null ? (
          // Aucun débit mesuré : la durée est INCONNUE, pas nulle.
          <div className="text-sm text-ink-500">{t('audit.reco.ingestion.unknown')}</div>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Metric
              label={t('audit.reco.ingestion')}
              value={t('audit.reco.ingestion.hours', { n: ingestion.hoursInitial })}
              sub={ingestion.bottleneck ? t('audit.reco.ingestion.bottleneck', { what: ingestion.bottleneck }) : undefined}
            />
            {ingestion.hoursNetwork != null && (
              <Metric label={t('audit.catalog.col.host')} value={t('audit.reco.ingestion.network', { n: ingestion.hoursNetwork })} />
            )}
            {ingestion.hoursOcr != null && (
              <Metric label="OCR" value={t('audit.reco.ingestion.ocr', { n: ingestion.hoursOcr })} />
            )}
            {ingestion.throughputMbps != null && (
              <Metric label={t('audit.reco.option.network')} value={fmtMbps(ingestion.throughputMbps)} />
            )}
          </div>
        )}
        {ingestion.bytes != null && (
          <div className="text-xs text-ink-500">{fmtBytes(ingestion.bytes)}</div>
        )}
      </Section>

      <Section title={t('audit.reco.assumptions')} description={t('audit.reco.assumptions.desc')}>
        <DataTable
          empty={t('audit.catalog.empty')}
          headers={[t('audit.catalog.col.detail'), t('audit.reco.col.score')]}
          rows={reco.assumptions.map(a => [
            <span title={a.code}>{a.label}</span>,
            `${a.value}${a.unit ? ` ${a.unit}` : ''}`,
          ])}
        />
      </Section>

      <Section title={t('audit.reco.priority')}>
        <DataTable
          empty={t('audit.catalog.empty')}
          headers={[
            t('audit.reco.col.rank'), t('audit.catalog.col.source'), t('audit.catalog.col.domain'),
            t('audit.reco.col.documents'), t('audit.catalog.col.feasibility'), t('audit.reco.col.score'),
          ]}
          rows={reco.prioritySources.map(ps => [
            ps.rank ?? '—',
            <span className="break-all">
              {ps.label}
              {ps.kind && <span className="text-ink-500"> · {contractLabel(t, 'audit.catalog.kind.' + ps.kind, ps.kind)}</span>}
            </span>,
            <>
              {ps.domains.join(', ') || '—'}
              {ps.sensitivityLevel && (
                <div className="mt-1">
                  <ToneBadge tone={sensitivityTone(ps.sensitivityLevel)}>
                    {contractLabel(t, 'audit.catalog.level.' + ps.sensitivityLevel, ps.sensitivityLevel)}
                  </ToneBadge>
                </div>
              )}
            </>,
            <span>
              {fmtCount(ps.documents)}
              {ps.bytes != null && <span className="text-ink-500"> · {fmtBytes(ps.bytes)}</span>}
            </span>,
            <ToneBadge tone={feasibilityTone(ps.feasibility)}>
              {contractLabel(t, 'audit.catalog.feasibility.' + ps.feasibility, ps.feasibility)}
            </ToneBadge>,
            ps.score ?? '—',
          ])}
        />
      </Section>

      <Section title={t('audit.reco.prerequisites')}>
        <DataTable
          empty={t('audit.catalog.empty')}
          headers={[t('audit.catalog.col.detail'), t('audit.reco.owner.metier'), t('audit.catalog.col.gap')]}
          rows={reco.prerequisites.map(pr => [
            <>
              <div className="font-medium">{pr.label}</div>
              <div className="text-xs text-ink-500">{pr.detail}</div>
            </>,
            contractLabel(t, 'audit.reco.owner.' + pr.owner, pr.owner),
            pr.blocking
              ? <ToneBadge tone="danger">{t('audit.reco.prereq.blocking')}</ToneBadge>
              : <span className="text-ink-500">—</span>,
          ])}
        />
      </Section>

      <Section title={t('audit.reco.risks')}>
        <DataTable
          empty={t('audit.catalog.empty')}
          headers={[t('audit.catalog.col.detail'), t('audit.catalog.col.gap'), t('audit.reco.risk.mitigation')]}
          rows={reco.risks.map(r => [
            <>
              <div className="font-medium">{r.label}</div>
              <div className="text-xs text-ink-500">{r.detail}</div>
            </>,
            <ToneBadge tone={severityTone(r.severity)}>
              {contractLabel(t, 'audit.reco.severity.' + r.severity, r.severity)}
            </ToneBadge>,
            r.mitigation ?? '—',
          ])}
        />
      </Section>

      <Section title={t('audit.reco.workstreams')}>
        <DataTable
          empty={t('audit.catalog.empty')}
          headers={[t('audit.catalog.col.name'), t('audit.catalog.col.detail'), t('audit.reco.workstream.deliverable')]}
          rows={reco.workstreams.map(w => [
            <>
              <div className="font-medium">{w.title}</div>
              <code className="text-xs text-ink-500">{w.id}</code>
              {w.effort && (
                <div className="mt-1"><Badge variant="neutral">{t('audit.reco.workstream.effort', { level: w.effort })}</Badge></div>
              )}
            </>,
            <>
              <div>{w.objective}</div>
              {w.dependsOn.length > 0 && (
                <div className="mt-1 text-xs text-ink-500">
                  {t('audit.reco.workstream.depends', { list: w.dependsOn.join(', ') })}
                </div>
              )}
            </>,
            w.deliverable ?? '—',
          ])}
        />
      </Section>

      <Section
        title={t('audit.reco.scorecard')}
        aside={reco.overall.domainsTotal != null
          ? <span>{t('audit.reco.scorecard.domains', {
            measured: reco.overall.domainsMeasured ?? 0,
            total: reco.overall.domainsTotal,
          })}</span>
          : undefined}
      >
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-7">
          {reco.scorecard.map(d => (
            <Card key={d.name} className={cn(d.blocking && 'border-rose-300 dark:border-rose-800')}>
              <div className={labelClassName()}>{contractLabel(t, 'audit.reco.domain.' + d.name, d.name)}</div>
              <div className="mt-1 font-display text-lg text-ink-900 dark:text-white" title={d.justification ?? undefined}>
                {/* Un domaine non mesuré n'a pas de note : il ne vaut PAS 0/5. */}
                {d.measured && d.score != null ? `${d.score}/5` : t('audit.reco.scorecard.not_measured')}
              </div>
              {d.justification && <div className="mt-1 line-clamp-3 text-xs text-ink-500">{d.justification}</div>}
            </Card>
          ))}
          <Card className="bg-brand-50 dark:bg-brand-900/20">
            <div className={labelClassName()}>{t('audit.reco.scorecard.overall')}</div>
            <div className="mt-1 font-display text-lg text-ink-900 dark:text-white">
              {reco.overall.score != null ? `${reco.overall.score}/5` : t('audit.reco.scorecard.not_measured')}
            </div>
          </Card>
        </div>
      </Section>

      <Section title={t('audit.reco.gaps')} description={t('audit.catalog.gaps.desc')}>
        <DataTable
          empty={t('audit.catalog.empty')}
          headers={[t('audit.catalog.col.gap'), t('audit.catalog.col.detail'), t('audit.reco.gap.impact')]}
          rows={reco.gaps.map(g => [
            <ToneBadge tone={severityTone(g.severity)}>
              {contractLabel(t, 'audit.catalog.severity.' + g.severity, g.severity)}
            </ToneBadge>,
            <span>
              <code className="text-xs">{g.code}</code>
              <div>{g.detail}</div>
            </span>,
            g.impact ?? '—',
          ])}
        />
      </Section>
    </div>
  );
}

function OptionCard({ option }: { option: HardwareOption }) {
  const { t } = useT();
  return (
    <Card className={cn('space-y-2', option.recommended && 'border-brand-400 ring-1 ring-brand-300 dark:border-brand-600')}>
      <div className="flex items-center gap-2">
        <h3 className="font-display text-base text-ink-900 dark:text-white">
          {contractLabel(t, 'audit.reco.tier.' + option.tier, option.tier)}
        </h3>
        {option.recommended && <Badge variant="success">{t('common.recommended')}</Badge>}
      </div>
      <dl className="space-y-1 text-sm">
        <Row label={t('audit.reco.option.model')} value={[option.modelClass, option.quantization].filter(Boolean).join(' · ') || '—'} />
        <Row label={t('audit.reco.option.gpu')} value={option.gpuClass ?? '—'} />
        <Row label={t('audit.reco.option.cpu')} value={option.cpuCores != null ? t('audit.reco.option.cores', { n: option.cpuCores }) : '—'} />
        <Row label={t('audit.reco.option.ram')} value={option.ramGb != null ? `${option.ramGb} GB` : '—'} />
        <Row label={t('audit.reco.option.storage')} value={[option.storageGb != null ? `${option.storageGb} GB` : null, option.storageClass].filter(Boolean).join(' ') || '—'} />
        <Row label={t('audit.reco.option.network')} value={fmtMbps(option.networkMbps)} />
        <Row label={t('audit.reco.option.users')} value={option.concurrentUsers != null ? String(option.concurrentUsers) : '—'} />
      </dl>
      {option.limits.length > 0 && (
        <div className="text-xs text-amber-700 dark:text-amber-300">
          <div className={labelClassName()}>{t('audit.reco.option.limits')}</div>
          <Bullets items={option.limits} />
        </div>
      )}
    </Card>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-2">
      <dt className="text-ink-500">{label}</dt>
      <dd className="text-right text-ink-900 dark:text-white">{value}</dd>
    </div>
  );
}

const fmtCount = (n: number | null): string => (n == null ? '—' : n.toLocaleString());
